// Drag-&-Drop-Multi-Upload für Nachreichungen (Dashboard).
// Sichtbar nur, wenn me.canUploadNachreichung === true (= submittet oder
// Bookings-Termin vorhanden). Eine FormData → multiple files in einem POST,
// max 5 files pro Request (API-Limit), 10 MB pro File.
//
// Bewusst leichtgewichtig — kein zusätzliches Dependency, nur HTML5 DnD
// + native input[type=file] mit `multiple`.

import { useCallback, useRef, useState } from 'react';
import { api, ApiError } from '../api/client.js';
import type { UploadMeta } from '@gql/shared-types';

const MAX_FILES_PER_BATCH = 5;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_MIMES = new Set<string>([
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
  'application/zip',
  'application/x-zip-compressed',
  'application/x-zip',
]);

interface Props {
  initialUploads: UploadMeta[];
  onUploaded?: (newCount: number, total: number) => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-DE', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function NachreichungUpload({ initialUploads, onUploaded }: Props) {
  const [uploads, setUploads] = useState<UploadMeta[]>(initialUploads);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    async (rawFiles: FileList | File[]) => {
      setError(null);
      setInfo(null);
      const files = Array.from(rawFiles);
      if (files.length === 0) return;

      if (files.length > MAX_FILES_PER_BATCH) {
        setError(
          `Maximal ${MAX_FILES_PER_BATCH} Dateien pro Hochladevorgang. Sie haben ${files.length} ausgewählt — bitte in mehreren Schritten hochladen.`,
        );
        return;
      }

      for (const f of files) {
        if (f.size > MAX_FILE_SIZE_BYTES) {
          setError(`"${f.name}" ist größer als 10 MB.`);
          return;
        }
        if (!ACCEPTED_MIMES.has(f.type.toLowerCase())) {
          setError(
            `"${f.name}" hat ein nicht erlaubtes Format (${f.type || 'unbekannt'}). Zulässig: PDF, JPG, PNG, HEIC, ZIP.`,
          );
          return;
        }
      }

      setIsUploading(true);
      try {
        const res = await api.uploadNachreichungen(files);
        const merged = [...res.uploads, ...uploads];
        setUploads(merged);
        const msg =
          res.uploads.length === 1
            ? `1 Datei hochgeladen.`
            : `${res.uploads.length} Dateien hochgeladen.`;
        setInfo(
          res.jobId
            ? `${msg} Wird in Ihre JurNodes-Akte übertragen.`
            : `${msg} Wird zur Sichtung bereitgestellt.`,
        );
        onUploaded?.(res.uploads.length, merged.length);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.code === 'unsupported_media_type') {
            setError('Dateityp nicht erlaubt. Zulässig: PDF, JPG, PNG, HEIC, ZIP.');
          } else if (err.code === 'nachreichung_not_allowed') {
            setError(
              'Nachreichungen sind erst nach Absenden des Onboardings oder Buchung eines Termins möglich.',
            );
          } else if (err.code === 'no_files') {
            setError('Keine Datei ausgewählt.');
          } else {
            setError(`Upload-Fehler (${err.status} ${err.code}).`);
          }
        } else {
          setError('Upload fehlgeschlagen. Bitte erneut versuchen.');
        }
      } finally {
        setIsUploading(false);
      }
    },
    [uploads, onUploaded],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        void handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles],
  );

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const onDelete = useCallback(async (id: string) => {
    setError(null);
    setInfo(null);
    try {
      await api.deleteNachreichung(id);
      setUploads((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      if (err instanceof ApiError && err.code === 'upload_not_found_or_already_pushed') {
        setError('Datei wurde bereits an die Kanzlei übertragen und kann nicht mehr gelöscht werden.');
      } else {
        setError('Löschen fehlgeschlagen.');
      }
    }
  }, []);

  return (
    <div className="nachreichung-upload">
      <div
        className={`dropzone${isDragging ? ' dropzone-active' : ''}${isUploading ? ' dropzone-busy' : ''}`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        aria-label="Dateien hochladen — Datei hierher ziehen oder klicken"
      >
        <div className="dropzone-icon" aria-hidden="true">📎</div>
        <div className="dropzone-text">
          {isUploading ? (
            <strong>Lädt hoch …</strong>
          ) : isDragging ? (
            <strong>Loslassen zum Hochladen</strong>
          ) : (
            <>
              <strong>Dateien hierher ziehen</strong> oder klicken, um Dateien auszuwählen
            </>
          )}
        </div>
        <div className="dropzone-hint">
          Bis zu {MAX_FILES_PER_BATCH} Dateien gleichzeitig, je max. 10&nbsp;MB.
          Erlaubt: PDF, JPG, PNG, HEIC, ZIP.
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,application/pdf,.jpg,.jpeg,image/jpeg,.png,image/png,.heic,.heif,image/heic,image/heif,.zip,application/zip,application/x-zip-compressed"
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files) void handleFiles(e.target.files);
          }}
          disabled={isUploading}
        />
      </div>

      {error && <div className="alert alert-error" style={{ marginTop: 12 }}>{error}</div>}
      {info && !error && <div className="alert alert-success" style={{ marginTop: 12 }}>{info}</div>}

      {uploads.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <h4 className="card-h" style={{ marginBottom: 8 }}>Bisher nachgereichte Dateien</h4>
          <ul className="upload-list">
            {uploads.map((u) => (
              <li key={u.id} className="upload-list-item">
                <div className="upload-list-meta">
                  <span className="upload-list-name">{u.filename}</span>
                  <span className="upload-list-size">{formatSize(u.size)}</span>
                  <span className="upload-list-date">{formatDate(u.createdAt)}</span>
                </div>
                <button
                  type="button"
                  className="button button-link"
                  onClick={() => void onDelete(u.id)}
                  aria-label={`Datei ${u.filename} löschen`}
                >
                  löschen
                </button>
              </li>
            ))}
          </ul>
          <p className="card-subtitle" style={{ marginTop: 12, fontSize: '0.9em' }}>
            Hinweis: Eine Datei kann nur gelöscht werden, solange sie noch nicht
            in Ihre JurNodes-Akte übernommen wurde.
          </p>
        </div>
      )}
    </div>
  );
}
